import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import type { Draft } from "immer";
import { ApiEndpoints, baseQueryWithAuth } from "../../core";
import { toast } from "../../core/toast";
import { EditGymRequest, Gym } from "../../types";
import { subscriptionLicenseApi } from "../subscription/subscriptionLicense.rtk.api";

interface ApiResponse<T = any> {
  success: boolean;
  message: string;
  result?: T;
  results?: T;
}

export const gymApi = createApi({
  reducerPath: "gymApi",
  baseQuery: baseQueryWithAuth,
  tagTypes: ["Gym", "Gyms"],

  endpoints: (builder) => ({
    // Get all gyms
    getGyms: builder.query<Gym[], string | { organisationId: string; license?: number } | void>({
      query: (params) => {
        let url = ApiEndpoints.getAllGyms;
        const queryParams = new URLSearchParams();

        if (typeof params === "string") {
          queryParams.append("organisation", params);
        } else if (params && typeof params === "object") {
          if (params.organisationId) {
            queryParams.append("organisation", params.organisationId);
          }
          if (params.license !== undefined) {
            queryParams.append("license", String(params.license));
          }
        }

        const queryString = queryParams.toString();
        if (queryString) {
          url += `?${queryString}`;
        }

        return {
          url,
          method: "GET",
        };
      },
      transformResponse: (res: any) => res?.results ?? res,
      providesTags: ["Gyms"],
    }),

    // Get gym by ID
    getGymById: builder.query<Gym, string>({
      query: (gymid) => ({
        url: `${ApiEndpoints.gym}/${gymid}`,
        method: "GET",
      }),
      transformResponse: (res: any): Gym => res?.result ?? res,
      providesTags: (_result, _error, gymid) => [{ type: "Gym", id: gymid }],
    }),

    // Create gym
    createGym: builder.mutation<ApiResponse<Gym>, EditGymRequest>({
      query: (data) => {
        const body: EditGymRequest = {
          name: data?.name,
          organisation: data?.organisation,
          state: data?.state,
          city: data?.city,
          location: data?.location,
          contact: data?.contact
            ? {
              name: data.contact.name,
              phone: String(data.contact.phone ?? ""),
              email: data.contact.email,
            }
            : undefined,
          screens: data?.screens
            ? {
              premium: Number(data.screens.premium ?? 0),
              basic: Number(data.screens.basic ?? 0),
            }
            : undefined,
          allowGuestAccess: data?.allowGuestAccess ?? false,
          ...(data?.allowGuestAccess === true && data?.geoLocation
            ? {
              geoLocation: {
                latitude: Number(data.geoLocation.latitude),
                longitude: Number(data.geoLocation.longitude),
              }
            }
            : {}),
          ...(data?.allowGuestAccess === true && data?.locationRangeInMeters
            ? {
              locationRangeInMeters: Number(data.locationRangeInMeters),
            }
            : {}),
            ...(data?.allowGuestAccess === true && data?.locationAccess
            ? {
              locationAccess: data.locationAccess,
            }
            : {}),
        };
        return {
          url: `${ApiEndpoints.createGym}`,
          method: "POST",
          body,
        };
      },
      invalidatesTags: ["Gyms"],
      async onQueryStarted(_arg, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
        } catch (error: any) {
          console.log(error.data.message);
        }
      },
    }),

    // Edit gym
    editGym: builder.mutation<
      ApiResponse<Gym>,
      { gymid: string; data: EditGymRequest }
    >({
      query: ({ gymid, data }) => {
        const body: EditGymRequest = {
          name: data?.name,
          organisation: data?.organisation,
          state: data?.state,
          city: data?.city,
          location: data?.location,
          contact: data?.contact
            ? {
              name: data.contact.name,
              phone: String(data.contact.phone ?? ""),
              email: data.contact.email,
            }
            : undefined,
          screens: data?.screens
            ? {
              premium: Number(data.screens.premium ?? 0),
              basic: Number(data.screens.basic ?? 0),
            }
            : undefined,
          autoRepeatSchedule: data?.autoRepeatSchedule,
          repeatPeriodType: data?.repeatPeriodType,
          allowGuestAccess: data?.allowGuestAccess,

          locationAccess: data?.locationAccess,
          locationRangeInMeters: data?.locationRangeInMeters != null ? Number(data.locationRangeInMeters) : undefined,
          ...(data?.geoLocation
            ? {
              geoLocation: {
                latitude: Number(data.geoLocation.latitude),
                longitude: Number(data.geoLocation.longitude),
              }
            }
            : {}),
        };
        return {
          url: `${ApiEndpoints.editGym}/${gymid}`,
          method: "PATCH",
          body,
        };
      },
      invalidatesTags: (_result, _error, { gymid }) => [
        { type: "Gym", id: gymid },
        "Gyms",
      ],
    }),

    // Delete gym
    deleteGym: builder.mutation<ApiResponse<{ ok: boolean }>, string>({
      query: (gymid) => ({
        url: `${ApiEndpoints.deleteGym}/${gymid}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Gyms"],
      async onQueryStarted(gymid, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          gymApi.util.updateQueryData(
            "getGyms",
            undefined,
            (draft: Draft<Gym[]>) => {
              return draft.filter((gym) => (gym._id || gym.gymid) !== gymid);
            }
          )
        );

        try {
          await queryFulfilled;
        } catch (error: any) {
          patchResult.undo();
        }
      },
    }),

    // Toggle gym status
    toggleGymStatus: builder.mutation<
      ApiResponse<{ ok: boolean }>,
      { gymid: string; status: "Active" | "Inactive" }
    >({
      query: ({ gymid, status }) => ({
        url: `${ApiEndpoints.gymStatusChange}/${gymid}`,
        method: "PATCH",
        body: { status },
      }),
      invalidatesTags: (_result, _error, { gymid }) => [
        { type: "Gym", id: gymid },
        "Gyms",
      ],
      async onQueryStarted({ gymid, status }, { dispatch, queryFulfilled }) {
        // Optimistic update in list
        const patchList = dispatch(
          gymApi.util.updateQueryData(
            "getGyms",
            undefined,
            (draft: Draft<Gym[]>) => {
              const gym = draft.find((g) => (g._id || g.gymid) === gymid);
              if (gym) gym.status = status;
            }
          )
        );

        // Optimistic update in detail (if cached)
        const patchDetail = dispatch(
          gymApi.util.updateQueryData(
            "getGymById",
            gymid,
            (draft: Draft<Gym>) => {
              if (draft) draft.status = status;
            }
          )
        );

        try {
          await queryFulfilled;
        } catch (error: any) {
          patchList.undo();
          patchDetail.undo();
        }
      },
    }),
    assignLicenses: builder.mutation<ApiResponse, { gymid: string; licenses: string[] }>({
      query: ({ gymid, licenses }) => ({
        url: `${ApiEndpoints.assignLicense}/${gymid}`,
        method: "PATCH",
        body: { licenses },
      }),
      invalidatesTags: (_result, _error, { gymid }) => [
        { type: "Gym", id: gymid },
        "Gyms",
      ],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          // Manually invalidate SubscriptionLicenses in subscriptionLicenseApi
          dispatch(
            subscriptionLicenseApi.util.invalidateTags(["SubscriptionLicenses"])
          );
        } catch (error: any) {
          // Error handled globally
        }
      },
    }),
    unAssignLicense: builder.mutation<
      ApiResponse,
      { licenseId: string; }
    >({
      query: ({ licenseId }) => ({
        url: `${ApiEndpoints.unAssignLicense}/${licenseId}`,
        method: "PATCH",
      }),
      invalidatesTags: (_result, _error, { licenseId }) => [
        { type: "Gym", id: licenseId },
        "Gyms",
      ],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          // Manually invalidate SubscriptionLicenses in subscriptionLicenseApi
          dispatch(
            subscriptionLicenseApi.util.invalidateTags(["SubscriptionLicenses"])
          );
        } catch (error: any) {
          // Error handled globally
        }
      },
    }),
  }),
});

export const {
  useGetGymsQuery,
  useGetGymByIdQuery,
  useCreateGymMutation,
  useEditGymMutation,
  useDeleteGymMutation,
  useToggleGymStatusMutation,
  useAssignLicensesMutation,
  useUnAssignLicenseMutation,
} = gymApi;
